/**
 * Split-view workspace state: is the second pane open, what is it showing, and
 * how wide is the divider set.
 *
 * Deliberately a tiny provider rather than component state, because three
 * unrelated places need it: the Header (the toggle and the "open in right
 * pane" affordance on every nav item), RootLayout (which lays the two panes
 * out), and the pane itself (which reports its own navigation back so a
 * reload — and the swap button — see where it actually is).
 *
 * Persisted to localStorage, NOT daily-scoped: a two-pane layout is a desk
 * setup, not a per-day toggle, and an operator who works recon beside the
 * blotter every morning should find it the way they left it.
 */
import React, { createContext, useCallback, useContext, useMemo } from "react";
import { usePersistentState } from "../hooks/usePersistentState";

const KEY = "ifm.splitView";

/** Primary pane's share of the width. Clamped so neither pane can be squeezed
 *  to a useless sliver — these screens carry wide tables. */
export const MIN_RATIO = 0.25;
export const MAX_RATIO = 0.75;

/** Below this the app is single-pane regardless of the stored preference: two
 *  of these tables side by side stop being readable, and the Header's own nav
 *  already collapses to a hamburger at `lg`. */
export const SPLIT_MIN_WIDTH = 1024;

export interface SplitViewValue {
  open: boolean;
  /** The right pane's current path, e.g. "/trading-desk/cash/holdings".
   *  Empty until a real page is chosen: the pane must never be handed "/" and
   *  left to the landing redirect (see SecondaryPane for why that blanks it). */
  path: string;
  /** Left pane's width fraction, 0..1. */
  ratio: number;
  openPane: (path?: string) => void;
  closePane: () => void;
  /** Called BY the pane when it navigates itself (sub-tab clicks, redirects). */
  setPanePath: (path: string) => void;
  setRatio: (ratio: number) => void;
}

interface Stored {
  open: boolean;
  path: string;
  ratio: number;
}

const DEFAULT: Stored = { open: false, path: "", ratio: 0.5 };

const clampRatio = (r: number): number =>
  Number.isFinite(r) ? Math.min(MAX_RATIO, Math.max(MIN_RATIO, r)) : DEFAULT.ratio;

// A stored blob written by an older build (or hand-edited) must never break
// the shell — anything unexpected falls back to the default shape.
const parse = (raw: string): Stored => {
  try {
    const v = JSON.parse(raw) as Partial<Stored> | null;
    if (!v || typeof v !== "object") return DEFAULT;
    return {
      open: v.open === true,
      // "/" is not a page — an older build stored it, and it must not come
      // back as one.
      path: typeof v.path === "string" && v.path.startsWith("/") && v.path !== "/" ? v.path : DEFAULT.path,
      ratio: clampRatio(typeof v.ratio === "number" ? v.ratio : DEFAULT.ratio),
    };
  } catch {
    return DEFAULT;
  }
};

const Ctx = createContext<SplitViewValue | null>(null);

export const SplitViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = usePersistentState<Stored>(KEY, DEFAULT, JSON.stringify, parse);

  const openPane = useCallback((path?: string) => {
    setState((s) => ({ ...s, open: true, path: path || s.path || DEFAULT.path }));
  }, [setState]);

  const closePane = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, [setState]);

  const setPanePath = useCallback((path: string) => {
    // Same-path writes are dropped so the pane reporting its own location on
    // every render cannot loop through localStorage.
    setState((s) => (s.path === path ? s : { ...s, path }));
  }, [setState]);

  const setRatio = useCallback((ratio: number) => {
    setState((s) => {
      const next = clampRatio(ratio);
      return s.ratio === next ? s : { ...s, ratio: next };
    });
  }, [setState]);

  const value = useMemo<SplitViewValue>(
    () => ({ ...state, openPane, closePane, setPanePath, setRatio }),
    [state, openPane, closePane, setPanePath, setRatio],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

/**
 * Split state. Returns an inert, permanently-closed value when used outside
 * the provider so a component can ask without caring whether the shell is
 * mounted (tests, the pane's own subtree).
 */
export const useSplitView = (): SplitViewValue => {
  const v = useContext(Ctx);
  return (
    v ?? {
      open: false,
      path: "",
      ratio: DEFAULT.ratio,
      openPane: () => {},
      closePane: () => {},
      setPanePath: () => {},
      setRatio: () => {},
    }
  );
};

/**
 * True while the viewport is wide enough for two panes. A stored `open` is
 * honoured only together with this, so shrinking the window (or opening the
 * PWA narrow) falls back to one pane instead of two unreadable ones.
 */
export const useWideEnoughForSplit = (): boolean => {
  const query = `(min-width: ${SPLIT_MIN_WIDTH}px)`;
  const [wide, setWide] = React.useState<boolean>(
    () => typeof window === "undefined" || window.matchMedia(query).matches,
  );
  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setWide(e.matches);
    setWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return wide;
};
